import { json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Flex } from '#app/components/layout/flex.tsx'
import { Separator } from '#app/components/layout/separator.tsx'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts, Usage } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Separator',
}

const component: componentProps = {
	name: 'Separator',
	shortName: 'Separator',
	fileName: 'separator',
	category: 'layout',
	description: [
		'Horizontal separators can be built with the HTML <hr> element. However, there is no HTML element for a vertical separator. This is an accessible implementation of a Separator that supports purely decorative separators, i.e. removed from the accessibility tree, and can be styled as needed.',
	],
	features: [
		'Supports horizontal and vertical orientations.',
		'Support visibility in the accessibility tree.',
		'Supports purely decorative separators, i.e. removed from the accessibility tree.',
	],
	parts: [
		{
			name: 'Separator',
			description: 'Visually or semantically separates content.',
			props: [
				{ name: 'orientation', type: "'horizontal' | 'vertical'", default: "'horizontal'", description: 'The orientation of the separator.' },
				{ name: 'decorative', type: 'boolean', default: 'false', description: 'Whether the separator is decorative or not.' },
			],
		},
	],
	usage: `<div className="border-muted-200 my-10 max-w-md rounded-lg border p-6">
	<h2 className="text-bold text-2xl font-bold">VerveUI</h2>
	<p className="text-muted-500 text-lg">A UI component library.</p>
	<Separator className="my-4" />
	<Flex gap="10" items="center" className="h-5">
		<p>Blog</p>
		<Separator orientation="vertical" decorative />
		<p>Docs</p>
		<Separator orientation="vertical" decorative />
		<p>Source</p>
	</Flex>
</div>`,
}

export const loader = async () => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const SeparatorRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, parts } = component

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<div className="border-muted-200 my-10 max-w-md rounded-lg border p-6">
				<h2 className="text-bold text-2xl font-bold">VerveUI</h2>
				<p className="text-muted-500 text-lg">A UI component library.</p>
				<Separator className="my-4" />
				<Flex gap="10" items="center" className="h-5">
					<p>Blog</p>
					<Separator orientation="vertical" decorative />
					<p>Docs</p>
					<Separator orientation="vertical" decorative />
					<p>Source</p>
				</Flex>
			</div>
			<Features features={features} />
			<Parts parts={parts} />
			<Source source={source} fileName={fileName} />
		</>
	)
}
export default SeparatorRoute
